"use client";

import { type ReactNode, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { useCookies } from "react-cookie";
import { useMediaQuery } from "react-responsive";

import { AppSidebar } from "@/layouts/DashboardLayout/components/app-sidebar.tsx";
import { SidebarInset, SidebarProvider } from "@/components/ui/sidebar";
import DashboardHeader from "@/layouts/DashboardLayout/components/DashboardHeader.tsx";
import CalendarHeader from "@/features/calendar/components/CalendarHeader.tsx";
import PATHS from "@/routes/paths.ts";
import EventsWrapper from "@/features/chat/wrappers/EventsWrapper.tsx";
import { PopupChatWrapper } from "@/features/chat/views/PopupChatWrapper.tsx";
import { isAlwaysCollapsedPage } from "@/hooks/use-sidebar-links.tsx";
import { useIsMobile } from "@/hooks/use-mobile.tsx";
import TemplateHeader from "@/features/calendar/components/templates/TemplateHeader";
import { HelpPopup } from "@/features/help/views/HelpPopup.tsx";

interface Props {
  children: ReactNode;
}

const DashboardLayout = ({ children }: Props) => {
  const location = useLocation();
  const [cookies] = useCookies();
  const isBigScreen = useMediaQuery({ minWidth: 1700 });
  const isCollapsedPage = isAlwaysCollapsedPage(location.pathname);
  const isMobile = useIsMobile();

  const header = useMemo(() => {
    if (
      location.pathname.startsWith(PATHS.STUDENT.WORKSPACES.WORKSPACES) &&
      location.pathname.endsWith("/schedule")
    ) {
      return <CalendarHeader />;
    }
    if (location.pathname.includes("/lesson/")) {
      return null;
    }
    if (new RegExp(`${PATHS.TUTOR.SCHEDULE.TEMPLATES}/.*/edit$`).test(location.pathname)) {
      return <TemplateHeader />;
    }
    switch (location.pathname) {
      case PATHS.TUTOR.DASHBOARD:
        return <DashboardHeader />;
      case PATHS.TUTOR.CALENDAR:
        return <CalendarHeader />;
      default:
        return <DashboardHeader />;
    }
  }, [location]);

  console.log(header);

  return (
    <SidebarProvider>
      <EventsWrapper>
        <AppSidebar className="text-muted-foreground border-sidebar-border/10 border-r" />
        <SidebarInset
          className={`overflow-hidden transition-all duration-200 ${
            isBigScreen
              ? cookies["sidebar:state"]
                ? isCollapsedPage
                  ? "ms-[4rem]"
                  : "ms-[14rem]"
                : "ms-[4rem]"
              : !isMobile && "ms-[4rem]"
          }`}
        >
          <div className={"relative z-50"}>{header}</div>
          {!location.pathname.includes("chat") && (
            <div className={"relative z-[100]"}>
              <PopupChatWrapper />
            </div>
          )}
          <div className="flex flex-1 flex-col gap-4">{children}</div>
          <HelpPopup />
        </SidebarInset>
      </EventsWrapper>
    </SidebarProvider>
  );
};

export default DashboardLayout;
